import React, { useEffect } from 'react';
import { getCategoryIcon, getCategoryOrder, stripIcons, getCategoryConfigObject } from '../../config/categoryConfig';

export default function ToolsPPEStep({ data, value, onChange }) {
    const {
        global_tools = [],
        global_ppes = [],
        task_metadata = {},
        config_waste = [],
        task_waste_mapping = {}
    } = data;
    const selectedTask = value.task;

    // Safe Accessors
    const safeTools = Array.isArray(global_tools) ? global_tools : [];
    const safePPEs = Array.isArray(global_ppes) ? global_ppes : [];
    const safeWasteConfig = Array.isArray(config_waste) ? config_waste : [];

    // Initialize state if empty
    useEffect(() => {
        // Only run if we have a task and the form is relatively empty (avoid overwriting user edits)
        // We check if tools/pbms/waste are ALL empty/missing before re-initializing
        const isFresh = (!value.tools || value.tools.length === 0) &&
            (!value.pbms || value.pbms.length === 0) &&
            (!value.waste || value.waste.length === 0);

        if (selectedTask && isFresh) {
            const metadata = task_metadata[selectedTask] || {};

            // 1. Tools: Handle legacy string vs array AND Filter stale tools
            let defaultTools = metadata.Gereedschap || [];
            if (typeof defaultTools === 'string') defaultTools = [defaultTools];

            // Critical Fix: Filter out tools that don't exist in the live global list anymore
            if (safeTools.length > 0) {
                defaultTools = defaultTools.filter(t =>
                    safeTools.some(gt => (typeof gt === 'string' ? gt : gt.name) === t)
                );
            }

            // 2. PPEs
            let defaultPPEs = metadata["PBM's"] || [];
            if (typeof defaultPPEs === 'string') defaultPPEs = [defaultPPEs];

            // 3. Waste (Initialize from mapping or metadata)
            let defaultWaste = [];

            // Check metadata (live field from Task Editor)
            if (metadata.waste_type) {
                defaultWaste = Array.isArray(metadata.waste_type) ? metadata.waste_type : [metadata.waste_type];
            }
            // Fallback: Legacy 'Afval' field
            else if (metadata.Afval) {
                defaultWaste = Array.isArray(metadata.Afval) ? metadata.Afval : [metadata.Afval];
            }
            // Fallback: Static mapping
            else if (task_waste_mapping[selectedTask]) {
                defaultWaste = [task_waste_mapping[selectedTask]];
            }

            // FILTER: Remove items not present in current config (Requested by USER)
            // config_waste can be strings or objects { label: 'foo' }
            if (safeWasteConfig.length > 0) {
                const validWasteLabels = safeWasteConfig.map(w => typeof w === 'object' ? w.label : w);
                defaultWaste = defaultWaste.filter(w => validWasteLabels.includes(w));
            }


            onChange({
                ...value,
                tools: defaultTools,
                pbms: defaultPPEs,
                waste: defaultWaste
            });
        }
    }, [selectedTask, safeTools, task_metadata]); // Depend on safeTools

    // --- HANDLERS ---

    const handleRemoveTool = (item) => {
        onChange({ ...value, tools: value.tools.filter(t => t !== item) });
    };

    const handleAddTool = (e) => {
        const item = e.target.value;
        if (item && !value.tools.includes(item)) {
            onChange({ ...value, tools: [...(value.tools || []), item] });
        }
        e.target.value = "";
    };

    const handleRemovePPE = (item) => {
        onChange({ ...value, pbms: value.pbms.filter(p => p !== item) });
    };

    const handleAddPPE = (e) => {
        const item = e.target.value;
        if (item && !value.pbms.includes(item)) {
            onChange({ ...value, pbms: [...(value.pbms || []), item] });
        }
        e.target.value = "";
    };

    const handleRemoveWaste = (item) => {
        onChange({ ...value, waste: value.waste.filter(w => w !== item) });
    };

    const handleAddWaste = (e) => {
        const item = e.target.value;
        if (item && (!value.waste || !value.waste.includes(item))) {
            onChange({ ...value, waste: [...(value.waste || []), item] });
        }
        e.target.value = "";
    };

    return (
        <div className="fade-in space-y-6">
            <h2 className="text-xl font-bold mb-4 text-white border-b border-slate-700 pb-2">Stap 3: Benodigdheden & Afval</h2>

            <p className="text-sm text-slate-400 mb-4">
                Controleer de benodigde middelen en afvalstromen voor deze taak.
            </p>

            {/* Tools Section */}
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-bold text-blue-400 mb-3 flex items-center gap-2">
                    🛠️ Gereedschap
                </h3>

                <div className="flex flex-col md:flex-row md:flex-wrap gap-2 mb-4">
                    {value.tools && value.tools.length > 0 ? (
                        value.tools.map((toolName, idx) => {
                            const toolObj = safeTools.find(t => (typeof t === 'string' ? t : t.name) === toolName);
                            const category = toolObj && typeof toolObj !== 'string' ? toolObj.category : null;
                            const icon = getCategoryIcon(category);

                            return (
                                <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-blue-900/40 border border-blue-700/50 text-blue-200 rounded-full text-sm w-fit max-w-full">
                                    <span className="opacity-75">{icon}</span>
                                    <span>{stripIcons(toolName)}</span>
                                    <button onClick={() => handleRemoveTool(toolName)} className="ml-1 hover:text-white">×</button>
                                </div>
                            );
                        })
                    ) : (
                        <span className="text-slate-500 italic text-sm">Geen gereedschap geselecteerd.</span>
                    )}
                </div>

                <div className="relative">
                    <select
                        className="input glass-input w-full md:w-1/2 text-sm"
                        onChange={handleAddTool}
                        defaultValue=""
                    >
                        <option value="" disabled>+ Voeg gereedschap toe...</option>
                        {(() => {
                            // Group tools by CANONICAL category to merge duplicates (e.g. "⚡ Elektro" & "Elektro")
                            const groupedTools = safeTools.reduce((acc, tool) => {
                                const toolName = typeof tool === 'string' ? tool : tool.name;
                                const rawCat = typeof tool === 'string' ? 'Overig' : (tool.category || 'Overig');

                                // Resolve to canonical label (e.g. "Elektro & Fijn")
                                const config = getCategoryConfigObject(rawCat);
                                const groupLabel = config ? config.label : rawCat;
                                const order = config ? config.order : 999;
                                const icon = config ? config.icon : getCategoryIcon(rawCat);

                                if (!acc[groupLabel]) {
                                    acc[groupLabel] = { label: groupLabel, order, icon, tools: [] };
                                }
                                acc[groupLabel].tools.push(toolName);
                                return acc;
                            }, {});

                            return Object.values(groupedTools)
                                .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
                                .map(group => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.tools.map(toolName => {
                                            if (value.tools?.includes(toolName)) return null;
                                            return <option key={toolName} value={toolName}>{group.icon} {stripIcons(toolName)}</option>;
                                        })}
                                    </optgroup>
                                ));
                        })()}
                    </select>
                </div>
            </div>

            {/* PPE Section */}
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-bold text-orange-400 mb-3 flex items-center gap-2">
                    🦺 Persoonlijke Beschermingsmiddelen (PBM's)
                </h3>

                <div className="flex flex-col md:flex-row md:flex-wrap gap-2 mb-4">
                    {value.pbms && value.pbms.length > 0 ? (
                        value.pbms.map((ppeName, idx) => {
                            const ppeObj = safePPEs.find(p => (typeof p === 'string' ? p : p.name) === ppeName);
                            const category = ppeObj && typeof ppeObj !== 'string' ? ppeObj.category : null;
                            const icon = getCategoryIcon(category);

                            return (
                                <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-orange-900/40 border border-orange-700/50 text-orange-200 rounded-full text-sm w-fit max-w-full">
                                    <span className="opacity-75">{icon}</span>
                                    <span>{stripIcons(ppeName)}</span>
                                    <button onClick={() => handleRemovePPE(ppeName)} className="ml-1 hover:text-white">×</button>
                                </div>
                            );
                        })
                    ) : (
                        <span className="text-slate-500 italic text-sm">Geen PBM's geselecteerd.</span>
                    )}
                </div>

                <div className="relative">
                    <select
                        className="input glass-input w-full md:w-1/2 text-sm"
                        onChange={handleAddPPE}
                        defaultValue=""
                    >
                        <option value="" disabled>+ Voeg PBM toe...</option>
                        {[...new Set(safePPEs.map(p => typeof p === 'string' ? 'Overig' : (p.category || 'Overig')))]
                            .sort((a, b) => getCategoryOrder(a) - getCategoryOrder(b))
                            .map(category => (
                                <optgroup key={category} label={category}>
                                    {safePPEs
                                        .filter(p => (typeof p === 'string' ? 'Overig' : (p.category || 'Overig')) === category)
                                        .map(p => {
                                            const name = typeof p === 'string' ? p : p.name;
                                            if (value.pbms?.includes(name)) return null;
                                            return <option key={name} value={name}>{getCategoryIcon(category)} {stripIcons(name)}</option>;
                                        })
                                    }
                                </optgroup>
                            ))}
                    </select>
                </div>
            </div>

            {/* Waste Section */}
            <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2">
                    ♻️ Afvalverwerking
                </h3>

                <div className="flex flex-col md:flex-row md:flex-wrap gap-2 mb-4">
                    {value.waste && value.waste.length > 0 ? (
                        value.waste.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-emerald-900/40 border border-emerald-700/50 text-emerald-200 rounded-full text-sm w-fit max-w-full">
                                <span>{item}</span>
                                <button onClick={() => handleRemoveWaste(item)} className="ml-1 hover:text-white">×</button>
                            </div>
                        ))
                    ) : (
                        <span className="text-slate-500 italic text-sm">Geen afvalstromen geselecteerd.</span>
                    )}
                </div>

                <div className="relative">
                    <select
                        className="input glass-input w-full md:w-1/2 text-sm"
                        onChange={handleAddWaste}
                        defaultValue=""
                    >
                        <option value="" disabled>+ Voeg afvalstroom toe...</option>
                        {safeWasteConfig.length > 0 ? (
                            safeWasteConfig.map((w, idx) => {
                                const label = typeof w === 'object' ? w.label : w;
                                const val = typeof w === 'object' ? w.label : w; // Use label as value
                                if (value.waste?.includes(val)) return null;
                                return <option key={idx} value={val}>{label}</option>;
                            })
                        ) : (
                            // Fallback defaults if config is empty
                            ['Restafval', 'Papier/Karton', 'Plastic', 'Metaal', 'Chemisch Afval', 'Olie/Vet'].map(w => (
                                <option key={w} value={w}>{w}</option>
                            ))
                        )}
                    </select>
                </div>
            </div>
        </div>
    );
}
